'use client';

import Link from 'next/link';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import BusMiniMap from './BusMiniMap';
import LandingIcon from './LandingIcon';
import ScheduleStory from './ScheduleStory';
import styles from './LandingHome.module.css';
import {
  HIDDEN_ROUTES, formatClock, niceTitle, onScroll, prefersReducedMotion, useSnapshots, viewportHeight, type LandingEvent,
} from './landingData';

const SLIDE = 0.55; // share of a screen spent sliding to the next word
const titleCase = (s: string) => s.replace(/ (And|Of|In|The|For) /g, (m) => m.toLowerCase());
const eventDate = (s: string) => new Date(s).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function gameName(title: string) {
  const m = title.match(/^(.*) at Rutgers (.*)$/);
  return m ? { sport: m[2].replace("Women's ", 'W. '), opponent: m[1] } : null;
}

function EventCard({ event, hidden }: { event: LandingEvent; hidden?: boolean }) {
  const game = gameName(event.title);
  return (
    <a
      className={styles.event}
      href={event.url ?? '/events'}
      target={event.url ? '_blank' : undefined}
      rel="noopener noreferrer"
      tabIndex={hidden ? -1 : undefined}
      aria-hidden={hidden || undefined}
    >
      {event.image ? (
        <div className={styles.eventImg} style={{ backgroundImage: `url("${event.image}")` }}>
          <span className={styles.eventDate}>{eventDate(event.start)}</span>
        </div>
      ) : (
        <div className={styles.eventGame}>
          <span className={styles.eventDate}>{eventDate(event.start)}</span>
          {game ? game.sport : event.category}
        </div>
      )}
      <div className={styles.eventText}>
        <b>{game ? `Rutgers vs. ${game.opponent}` : event.title}</b>
        <small>{event.where.split(',')[0] || event.host || ''}</small>
      </div>
    </a>
  );
}

/**
 * Pinned, horizontally scrolling feature words. Vertical scroll drives the track; the centered
 * word turns scarlet and shows a real-data panel. Schedule gets three screens, one per story step.
 */
export default function FeatureWordScroll() {
  const data = useSnapshots('schedule', 'day', 'home', 'bus', 'campus');
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const countRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  const [seen, setSeen] = useState<Set<number>>(() => new Set([0]));
  const [scheduleStep, setScheduleStep] = useState(0);
  const [chipsShown, setChipsShown] = useState(0);
  const [snipeOpen, setSnipeOpen] = useState(false);

  const routes = useMemo(() => data?.bus.routes.filter((r) => !HIDDEN_ROUTES.has(r.short)) ?? [], [data]);

  const items = useMemo(() => {
    if (!data) return [];
    const { schedule, day, home, bus, campus } = data;
    const sniper = schedule.sniper;
    const ca = schedule.schedule.find((c) => c.code === '01:198:211') ?? schedule.schedule[0];
    const activeRoutes = new Set(bus.buses.map((b) => b.route));
    const list: Array<{ word: string; weight: number; render: (index: number) => ReactNode }> = [
      {
        word: 'Schedule', weight: 3,
        render: () => <ScheduleStory schedule={schedule} step={scheduleStep} chipsShown={chipsShown} countRef={countRef} />,
      },
      {
        word: 'Snipe', weight: 1.2,
        render: () => {
          const watched = [
            { code: sniper.code, title: sniper.title, section: sniper.section, index: sniper.index, instructor: sniper.instructor, flips: true },
            ...schedule.schedule.filter((c) => c.code !== sniper.code).slice(0, 4).map((c) => ({
              code: c.code, title: c.title, section: c.section, index: c.index, instructor: c.instructor, flips: false,
            })),
          ];
          return (
            <div className={styles.split}>
              <div className={styles.big}>{watched.length}<small>sections watched</small></div>
              <div>
                <h3>Never miss a seat</h3>
                <p>Course Sniper watches closed sections and alerts you the moment a seat opens.</p>
                <div className={styles.snipeList}>
                  {watched.map((w) => {
                    const open = w.flips && snipeOpen;
                    return (
                      <div key={w.index} className={`${styles.snipeRow} ${open ? styles.snipeRowOpen : ''}`}>
                        <span className={styles.snipeCourse}><b>{niceTitle(w.title)}</b><small>{w.code} · sec {w.section} · {w.instructor}</small></span>
                        <span className={open ? styles.snipeOpen : styles.watching}>{open ? 'OPEN' : 'WATCHING'}</span>
                      </div>
                    );
                  })}
                </div>
                <Link className={styles.go} href="/course-sniper">Open sniper →</Link>
              </div>
            </div>
          );
        },
      },
      {
        word: 'Ratings', weight: 1.4,
        render: (index) => (
          <div className={styles.split}>
            <div className={styles.big}>{(ca.sirs ?? ca.courseSirs ?? 0).toFixed(1)}<small>SIRS · overall quality</small></div>
            <div>
              <h3>{ca.instructor} · {niceTitle(ca.title)}</h3>
              <p>Ratings from {schedule.stats.sirsCourses.toLocaleString()} courses&apos; worth of Rutgers SIRS evaluations, shown right next to every section.</p>
              <Link className={styles.go} href="/sirs">Open ratings →</Link>
              <div className={styles.profs}>
                {home.professors.map((p) => (
                  <Link key={p.name} className={styles.prof} href="/professors">
                    <div className={styles.photo}>
                      {seen.has(index) ? (
                        // eslint-disable-next-line @next/next/no-img-element -- official portraits hosted on rutgers.edu
                        <img src={p.photo} alt={p.name} loading="lazy" referrerPolicy="no-referrer" />
                      ) : <span className={styles.initials}>{p.name.split(' ').map((s) => s[0]).join('')}</span>}
                      <span className={styles.like}><LandingIcon name="heart" /></span>
                    </div>
                    <b>{p.name}</b>
                    <small>{titleCase(p.course)}</small>
                    <span className={styles.stars}>★ {p.rating.toFixed(2)}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ),
      },
      {
        word: 'Buses', weight: 1.4,
        render: (index) => (
          <div className={styles.centered}>
            <div className={`${styles.split} ${styles.splitCenter}`}>
              <div className={styles.big}>{bus.buses.length}<small>buses · {activeRoutes.size} routes</small></div>
              <div>
                <h3>Live on every campus</h3>
                <p>Real-time positions and stop arrivals from TripShot, overlaid on your day.</p>
                <div className={styles.pillRow}>
                  {routes.filter((r) => activeRoutes.has(r.id)).map((r) => (
                    <span key={r.id} className={styles.pill} style={{ ['--c' as string]: r.color }}>{r.short}</span>
                  ))}
                </div>
                <Link className={styles.go} href="/bus-tracker">Open buses →</Link>
              </div>
            </div>
            <BusMiniMap bus={bus} campus={campus} routes={routes} active={active === index} />
          </div>
        ),
      },
      {
        word: 'Dining', weight: 1,
        render: () => (
          <div className={styles.split}>
            <div className={styles.big}>{day.dining.items.length}<small>{day.dining.meal.toLowerCase()} picks · Livingston</small></div>
            <div>
              <h3>{day.dining.meal} at {day.dining.hall}</h3>
              <p>Every hall&apos;s menus with calories and dietary filters:</p>
              <div className={styles.rows}>
                {day.dining.items.slice(0, 4).map((item) => <div key={item.name}><span>{item.name}</span><span>{item.calories ?? '—'} cal</span></div>)}
              </div>
              <Link className={styles.go} href="/dining">Open dining →</Link>
            </div>
          </div>
        ),
      },
      {
        word: 'Rooms', weight: 1,
        render: () => (
          <div className={styles.split}>
            <div className={styles.big}>{day.rooms.freeCount}<small>of {day.rooms.totalRooms} rooms free</small></div>
            <div>
              <h3>Open classrooms · {day.rooms.when}</h3>
              <p>Built from every scheduled class meeting, so you can find somewhere to study between classes.</p>
              <div className={styles.rows}>
                {day.rooms.examples.slice(0, 3).map((r) => <div key={r.label}><span>{r.label}</span><span>{r.freeUntil ? `free until ${formatClock(r.freeUntil)}` : 'free rest of day'}</span></div>)}
              </div>
              <Link className={styles.go} href="/room-availability">Open rooms →</Link>
            </div>
          </div>
        ),
      },
      {
        word: 'Events', weight: 1.4,
        render: (index) => (
          <>
            <div className={styles.split}>
              <div className={styles.big}>{home.events.length}<small>picks this month</small></div>
              <div>
                <h3>Clubs, games and campus life</h3>
                <p>Every club event and home game in one feed, with times and places.</p>
                <Link className={styles.go} href="/events">Open events →</Link>
              </div>
            </div>
            {seen.has(index) ? (
              <div className={styles.eventRows}>
                {[
                  { key: 'a', list: home.events, speed: '70s', cls: '' },
                  { key: 'b', list: [...home.events.slice(Math.ceil(home.events.length / 2)), ...home.events.slice(0, Math.ceil(home.events.length / 2))], speed: '95s', cls: styles.eventRowB },
                ].map((row) => (
                  <div key={row.key} className={`${styles.eventMarquee} ${row.cls}`} aria-hidden={row.key === 'b' || undefined}>
                    <div className={`${styles.lane} ${styles.eventLane}`} style={{ ['--speed' as string]: row.speed }}>
                      {row.list.map((e) => <EventCard key={e.title} event={e} hidden={row.key === 'b'} />)}
                      {row.list.map((e) => <EventCard key={`dup-${e.title}`} event={e} hidden />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        ),
      },
    ];
    return list;
  }, [data, routes, scheduleStep, chipsShown, seen, active, snipeOpen]);

  const weights = items.map((it) => it.weight);
  const total = weights.reduce((a, b) => a + b, 0);
  const starts = weights.map((_, i) => weights.slice(0, i).reduce((a, b) => a + b, 0));

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track || !items.length || !data) return;
    const words = [...track.children] as HTMLElement[];
    let centers: number[] = [];
    const measure = () => { centers = words.map((w) => w.offsetLeft + w.offsetWidth / 2); };
    const ease = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t);
    const update = () => {
      if (!centers.length) measure();
      const vh = viewportHeight();
      const rect = section.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, -rect.top / Math.max(1, section.offsetHeight - vh)));
      const u = p * total;
      let i = 0;
      starts.forEach((s, k) => { if (u >= s) i = k; });
      const local = u - starts[i];
      const w = weights[i];
      const slide = i < items.length - 1 ? Math.min(1, Math.max(0, (local - (w - SLIDE)) / SLIDE)) : 0;
      const next = centers[i + 1] ?? centers[i];
      const x = centers[i] + (next - centers[i]) * ease(slide);
      track.style.transform = `translateX(${section.clientWidth / 2 - x - track.offsetLeft}px)`;
      const shown = slide > 0.5 ? i + 1 : i;
      setActive(shown);
      setSeen((prev) => (prev.has(shown) ? prev : new Set(prev).add(shown)));
      if (i === 0) {
        const story = Math.min(1, local / (w - SLIDE));
        const step = Math.min(2, Math.floor(story * 3));
        const sub = story * 3 - step;
        setScheduleStep(step);
        setChipsShown(step > 0 ? 99 : Math.floor(sub / 0.16 + 0.7));
        if (countRef.current && data) {
          const n = data.schedule.conflictFreeOptions;
          countRef.current.textContent = (step === 1 ? Math.round(n * Math.min(1, sub * 1.8)) : n).toLocaleString();
        }
      }
    };
    measure();
    update();
    const off = onScroll(() => { measure(); update(); });
    document.fonts?.ready.then(() => { measure(); update(); }).catch(() => {});
    return off;
    // starts/weights derive from items; keep the handler stable per data load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, items.length, total]);

  const snipeIndex = items.findIndex((it) => it.word === 'Snipe');
  const snipeActive = snipeIndex >= 0 && active === snipeIndex;
  useEffect(() => {
    if (!snipeActive) { setSnipeOpen(false); return; }
    const t = window.setTimeout(() => setSnipeOpen(true), prefersReducedMotion() ? 0 : 1600);
    return () => window.clearTimeout(t);
  }, [snipeActive]);

  const skip = () => {
    const next = sectionRef.current?.nextElementSibling;
    next?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };

  if (!data) return <section id="everything" className={styles.words} style={{ height: '100vh' }} aria-busy="true" />;

  return (
    <section id="everything" ref={sectionRef} className={styles.words} style={{ height: `${(total + 1) * 100}vh` }} aria-label="Everything in Scarlet Sync">
      <div className={styles.wordsSticky}>
        <div className={styles.wordsHead}>
          <span className={styles.kickerMono}>Everything in Scarlet Sync</span>
          <span className={styles.wordsHeadEnd}>
            <span className={styles.kickerMono}>{String(active + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}</span>
            <button type="button" className={styles.skip} onClick={skip} aria-label="Skip the feature tour and continue down the page">Skip ↓</button>
          </span>
        </div>
        <div className={`${styles.snipeToast} ${snipeActive && snipeOpen ? styles.snipeToastOn : ''}`} role="status" aria-live="polite" aria-hidden={!(snipeActive && snipeOpen) || undefined}>
          <span className={styles.snipeToastIcon}><LandingIcon name="target" /></span>
          <span className={styles.snipeToastText}>
            <b>Your class just opened!</b>
            <small>{niceTitle(data.schedule.sniper.title)} · Sec {data.schedule.sniper.section} · Index {data.schedule.sniper.index}</small>
          </span>
        </div>
        <div ref={trackRef} className={styles.track} aria-hidden="true">
          {items.map((it, i) => (
            <div key={it.word} className={`${styles.word} ${i === active ? styles.wordOn : ''} ${Math.abs(i - active) === 1 ? styles.wordNear : ''}`}>{it.word}</div>
          ))}
        </div>
        <div className={styles.panels}>
          {items.map((it, i) => (
            <article key={it.word} className={`${styles.panel} ${i === active ? styles.panelOn : ''}`} inert={i !== active || undefined} aria-label={it.word}>
              {it.render(i)}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
